import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { TimerReset, AlertCircle } from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as ss from 'simple-statistics';
import { ParsedWorksheet } from './worksheet';

interface TimeSeriesForecastingProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

const formatNumber = (value: number) => (Number.isFinite(value) ? value.toFixed(4) : '—');

export const TimeSeriesForecasting: React.FC<TimeSeriesForecastingProps> = ({ worksheet, selectedColumnId }) => {
  const [seriesColumnId, setSeriesColumnId] = useState(selectedColumnId ?? worksheet.numericColumns[0]?.id ?? '');
  const [method, setMethod] = useState<'moving-average' | 'exponential-smoothing' | 'trend'>('exponential-smoothing');
  const [windowSize, setWindowSize] = useState('3');
  const [alpha, setAlpha] = useState('0.35');
  const [horizon, setHorizon] = useState('6');

  const seriesColumn = worksheet.numericColumns.find((column) => column.id === seriesColumnId) ?? worksheet.numericColumns[0];
  const series = seriesColumn?.numericValues ?? [];

  const model = useMemo(() => {
    const forecastHorizon = Math.max(1, Number(horizon) || 1);
    if (series.length < 3) return null;

    const actualData = series.map((value, index) => ({ period: index + 1, actual: value, fitted: null as number | null, forecast: null as number | null }));
    let fitted: number[] = [];
    let forecasts: number[] = [];

    if (method === 'moving-average') {
      const size = Math.max(2, Math.min(series.length - 1, Number(windowSize) || 2));
      fitted = series.map((_, index) => {
        if (index < size) return Number.NaN;
        return ss.mean(series.slice(index - size, index));
      });
      const lastWindow = series.slice(-size);
      const nextValue = ss.mean(lastWindow);
      forecasts = Array.from({ length: forecastHorizon }, () => nextValue);
    }

    if (method === 'exponential-smoothing') {
      const smooth = Math.min(0.99, Math.max(0.01, Number(alpha) || 0.35));
      const smoothed: number[] = [series[0]];
      for (let index = 1; index < series.length; index += 1) {
        smoothed.push(smooth * series[index - 1] + (1 - smooth) * smoothed[index - 1]);
      }
      fitted = smoothed;
      forecasts = Array.from({ length: forecastHorizon }, () => smoothed[smoothed.length - 1]);
    }

    if (method === 'trend') {
      const points = series.map((value, index) => [index + 1, value] as [number, number]);
      const line = ss.linearRegression(points);
      fitted = series.map((_, index) => line.m * (index + 1) + line.b);
      forecasts = Array.from({ length: forecastHorizon }, (_, index) => line.m * (series.length + index + 1) + line.b);
    }

    const chartData = actualData.map((point, index) => ({ ...point, fitted: Number.isFinite(fitted[index]) ? fitted[index] : null }));
    forecasts.forEach((value, index) => {
      chartData.push({ period: series.length + index + 1, actual: null, fitted: null, forecast: value });
    });

    const mseBase = fitted
      .map((value, index) => (Number.isFinite(value) ? (series[index] - value) ** 2 : null))
      .filter((value): value is number => value !== null);
    const maeBase = fitted
      .map((value, index) => (Number.isFinite(value) ? Math.abs(series[index] - value) : null))
      .filter((value): value is number => value !== null);

    const mean = ss.mean(series);
    const trendSlope = ss.linearRegression(series.map((value, index) => [index + 1, value] as [number, number])).m;

    return {
      chartData,
      forecasts,
      mse: mseBase.length ? ss.mean(mseBase) : NaN,
      mae: maeBase.length ? ss.mean(maeBase) : NaN,
      mean,
      trendSlope,
      latest: series[series.length - 1],
      forecastHorizon,
    };
  }, [alpha, horizon, method, series, windowSize]);

  if (worksheet.numericColumns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No numeric time series column</AlertTitle>
        <AlertDescription>Paste numeric sequential data to run forecasting methods.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-blue-700 p-3 text-white">
          <TimerReset size={24} />
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge>Ready</Badge>
            <Badge variant="outline">Forecasting</Badge>
          </div>
          <h2 className="text-2xl font-semibold text-foreground">Time Series + Forecasting</h2>
          <p className="text-muted-foreground">Model sequential data with moving averages, exponential smoothing, and linear trend forecasting.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Forecast configuration</CardTitle>
          <CardDescription>Select the series column, choose a forecasting method, and tune its parameters.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="space-y-2 xl:col-span-2">
            <Label>Series column</Label>
            <Select value={seriesColumnId} onValueChange={setSeriesColumnId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {worksheet.numericColumns.map((column) => (
                  <SelectItem key={column.id} value={column.id}>{column.name} ({column.numericCount})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Method</Label>
            <Select value={method} onValueChange={(value: 'moving-average' | 'exponential-smoothing' | 'trend') => setMethod(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="moving-average">Moving average</SelectItem>
                <SelectItem value="exponential-smoothing">Exponential smoothing</SelectItem>
                <SelectItem value="trend">Linear trend</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Window / Alpha</Label>
            {method === 'moving-average' ? (
              <Input value={windowSize} onChange={(event) => setWindowSize(event.target.value)} />
            ) : method === 'exponential-smoothing' ? (
              <Input value={alpha} onChange={(event) => setAlpha(event.target.value)} />
            ) : (
              <Input value="Auto trend" disabled />
            )}
          </div>
          <div className="space-y-2">
            <Label>Forecast horizon</Label>
            <Input value={horizon} onChange={(event) => setHorizon(event.target.value)} />
          </div>
        </CardContent>
      </Card>

      {!model ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Need more observations</AlertTitle>
          <AlertDescription>The selected series needs at least three values to fit a forecast model.</AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Latest value</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber(model.latest)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Series mean</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber(model.mean)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Trend slope</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber(model.trendSlope)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">MAE</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber(model.mae)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">MSE</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber(model.mse)}</div></CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Forecast plot</CardTitle>
              <CardDescription>Actual series, fitted values, and forward forecast for the selected horizon.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={model.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="actual" stroke="hsl(var(--primary))" strokeWidth={2} dot />
                    <Line type="monotone" dataKey="fitted" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} strokeDasharray="6 4" />
                    <Line type="monotone" dataKey="forecast" stroke="hsl(var(--destructive))" strokeWidth={2} dot strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Forward forecast</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
              {model.forecasts.map((value, index) => (
                <div key={`forecast-${index}`} className="rounded-lg border p-3 text-sm">
                  <div className="text-muted-foreground">Period {series.length + index + 1}</div>
                  <div className="mt-1 text-lg font-semibold">{formatNumber(value)}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
